// Externals
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Libraries
import { logNewRelicPageAction, navigateTo } from '@makemydeal/dr-activities-common';
// DEV ONLY — commented out because full-page scroll (F304636) is disconnected below.
// import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    CustomerCardState,
    FEATURES,
    routingConfig,
    type DealSummaryWorkflowStatus,
    type ShowroomSectionKey
} from '@makemydeal/dr-sales-checkin-types';

import {
    addCoBuyer,
    dealSummaryRouteMap,
    removeCoBuyer,
    setCustomerVehiclesSlideoutVisibility,
    setFinancingInitialTab,
    viewCoBuyerInfo,
    viewCustomerInbox,
    viewCustomerInfo,
    getShowroomWorkflowStatus,
    ddjOpened,
    setHamburgerVisibility,
    activateSection,
    isFullPageScrollEnabled,
    useIsMissingVehicle,
    isTradeInCompleted
} from '@makemydeal/dr-sales-checkin-store';

import { useOpenTakeover } from './useOpenTakeover';

// Consts/Enums
import {
    Cobuyer,
    CobuyerInfo,
    CobuyerInbox,
    Inbox,
    RecentDeals,
    InventoryPath,
    Finance,
    Insurance,
    RemoveCoBuyer,
    Tradein,
    Username,
    VehicleDetails,
    DealSummary,
    ManageDocumentsPath
} from '../constants';
import { TRADE_SUMMARY, TRADE_VEHICLE_INFO, updateTradeInCurrentLocation } from '@makemydeal/dr-activities-tradein';

export const useNavigationHandler = () => {
    const dispatch = useDispatch();
    const workflowStatus = useSelector(getShowroomWorkflowStatus);
    const isFullScrollView = useSelector(isFullPageScrollEnabled);
    const isMissingVehicle = useIsMissingVehicle();
    const openTakeover = useOpenTakeover();

    const isCompleted = useSelector(isTradeInCompleted);
    const currentRoute = isCompleted ? TRADE_SUMMARY : TRADE_VEHICLE_INFO;

    const goToSection = useCallback(
        (key: ShowroomSectionKey, route: string) => {
            if (isFullScrollView) {
                dispatch(activateSection(key));
                dispatch(navigateTo(routingConfig.BUILD_YOUR_OFFER));
                return;
            }

            dispatch(navigateTo(route));
        },
        [dispatch, isFullScrollView]
    );

    const handleCobuyer = useCallback(() => {
        dispatch(addCoBuyer(true, CustomerCardState.SEARCH));
    }, [dispatch]);

    const handleUsername = useCallback(() => {
        dispatch(viewCustomerInfo(CustomerCardState.DETAILS));
    }, [dispatch]);

    const handleCobuyerInfo = useCallback(() => {
        dispatch(viewCoBuyerInfo(CustomerCardState.DETAILS));
    }, [dispatch]);

    const handleInbox = useCallback(() => {
        logNewRelicPageAction('SCI:CustomerCardPreloader - Inbox Click', {
            msFromPageLoad: Math.round(performance.now()),
            isCoBuyer: false
        });
        dispatch(viewCustomerInbox(false));
    }, [dispatch]);

    const handleCobuyerInbox = useCallback(() => {
        logNewRelicPageAction('SCI:CustomerCardPreloader - Inbox Click', {
            msFromPageLoad: Math.round(performance.now()),
            isCoBuyer: true
        });
        dispatch(viewCustomerInbox(true));
    }, [dispatch]);

    const handleInventory = useCallback(() => {
        dispatch(navigateTo(routingConfig.INVENTORY));
    }, [dispatch]);

    const handleRemoveCobuyer = useCallback(() => {
        dispatch(removeCoBuyer());
    }, [dispatch]);

    const handleTradein = useCallback(() => {
        if (!isFullScrollView) {
            dispatch(updateTradeInCurrentLocation(currentRoute));
        }
        goToSection(FEATURES.TRADE_IN, routingConfig.TRADE_IN);
    }, [goToSection]);

    const handleFinance = useCallback(() => {
        dispatch(setFinancingInitialTab('buyer'));
        goToSection(FEATURES.CUSTOMER_DETAILS, routingConfig.FINANCING);
    }, [dispatch, goToSection]);

    const handleInsurance = useCallback(() => {
        dispatch(setFinancingInitialTab('insurance'));

        // Insurance is a deep link into a sub-tab, not a section to scroll to, so under full-page
        // scroll it opens the takeover directly instead of landing on the stack via `goToSection`.
        if (isFullScrollView) {
            openTakeover(FEATURES.CUSTOMER_DETAILS);
            return;
        }

        dispatch(navigateTo(routingConfig.FINANCING));
    }, [dispatch, isFullScrollView, openTakeover]);

    const handleVehicleDetails = useCallback(() => {
        if (isMissingVehicle && isFullScrollView) {
            dispatch(navigateTo(routingConfig.INVENTORY));
            return;
        }

        goToSection(FEATURES.VEHICLE_DETAILS, routingConfig.VEHICLE_DETAILS);
    }, [dispatch, goToSection, isMissingVehicle, isFullScrollView]);

    const handleDealSummary = useCallback(() => {
        const targetRoute = dealSummaryRouteMap[workflowStatus as DealSummaryWorkflowStatus] ?? routingConfig.DEAL_SUMMARY_REQUEST;

        goToSection(FEATURES.DEAL_SUMMARY, targetRoute);
    }, [goToSection, workflowStatus]);

    const handleManageDocuments = useCallback(() => {
        dispatch(ddjOpened());
        dispatch(setHamburgerVisibility(false));
        dispatch(navigateTo(routingConfig.MANAGE_DOCUMENTS));
    }, [dispatch]);

    const handleRecentDeals = useCallback(() => {
        dispatch(setCustomerVehiclesSlideoutVisibility(true));
    }, [dispatch]);

    // Use useMemo to prevent handlers object recreation on every render
    const handlers = useMemo(
        () => ({
            [Cobuyer]: handleCobuyer,
            [Username]: handleUsername,
            [CobuyerInfo]: handleCobuyerInfo,
            [Inbox]: handleInbox,
            [CobuyerInbox]: handleCobuyerInbox,
            [RecentDeals]: handleRecentDeals,
            [InventoryPath]: handleInventory,
            [RemoveCoBuyer]: handleRemoveCobuyer,
            [Tradein]: handleTradein,
            [Finance]: handleFinance,
            [Insurance]: handleInsurance,
            [VehicleDetails]: handleVehicleDetails,
            [DealSummary]: handleDealSummary,
            [ManageDocumentsPath]: handleManageDocuments
        }),
        [
            handleCobuyer,
            handleUsername,
            handleCobuyerInfo,
            handleInbox,
            handleCobuyerInbox,
            handleRecentDeals,
            handleInventory,
            handleRemoveCobuyer,
            handleTradein,
            handleFinance,
            handleInsurance,
            handleVehicleDetails,
            handleDealSummary,
            handleManageDocuments
        ]
    );

    // Main handler function that routes to the appropriate specific handler
    const handleNavigation = useCallback(
        (navItem: string) => {
            const handler = handlers[navItem as keyof typeof handlers];
            if (handler) {
                handler();
            }
        },
        [handlers]
    );

    return {
        handlers,
        handleNavigation
    };
};
